import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, TooltipItem  } from 'chart.js';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { InstrumentPredictionIntervalOut, InstrumentPredictionOut, ModelPortfolioInstruments } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import tippy from 'tippy.js';
import { mapIsinsToWeights } from '../../functions';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { InstrumentChartCardInput, LanguagesEnum } from '../charts';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/browser';

@Component({
  selector: 'libero-history-chart-card',
  templateUrl: './history-chart-card.component.html',
  styleUrls: ['./history-chart-card.component.scss'],
  standalone: false
})
export class HistoryChartCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public cardData?: InstrumentChartCardInput;
  @Input() public userLocalization: keyof typeof LanguagesEnum = 'cs-CZ';

  public chartData: InstrumentPredictionOut | null = null;
  public selectedChartData?: ChartConfiguration['data'];
  public dropdownData: {humanName: string, performance: number }[] = [];
  public selectedYears: 'oneYear' | 'threeYears' | 'fiveYears' = 'oneYear';
  public availableYearsBack: 1 | 3 | 5 = 1;
  public noDataAvailable = false;

  private changesSubject = new Subject<SimpleChanges>();
  private changesSub?: Subscription;

  // tooltips
  @ViewChild('button3YContent', { static: false }) public disabledButton3YContent!: ElementRef;
  @ViewChild('tooltip3YContainerEl', { static: false }) public tooltip3YContainer!: ElementRef;
  @ViewChild('button5YContent', { static: false }) public disabledButton5YContent!: ElementRef;
  @ViewChild('tooltip5YContainerEl', { static: false }) public tooltip5YContainerEl!: ElementRef;

  public preloader = true;
  public loadError = false;
  public wrongWeights = false;
  public weightNull = false;

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        position: 'average',
        callbacks: {
          title: (tooltipItems: TooltipItem<'line'>[]) => {
            dayjs.locale(this.userLocalization === 'en-GB' ? 'en' : 'cs');
            const title = tooltipItems?.[0]?.label;
            const date = dayjs(title);

            if (date.isValid()) {
              return date.format('D. MMM. YYYY');
            }
            return title;
          },
          label: (tooltipItem: TooltipItem<'line'>) => {
            try {
              const value = tooltipItem.raw as {x: number, y: number};

              const formattedValue = new Intl.NumberFormat(this.userLocalization, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(value.y);

              return formattedValue + ' %';
            } catch (error) {
              // used to debugg "Value negative out of range for Intl.NumberFormat options property signDisplay" error
              Sentry.addBreadcrumb({ data: { userLocalization: this.userLocalization } });
              Sentry.addBreadcrumb({ data: { tooltipItem } });
              Sentry.addBreadcrumb({ data: { error } });
              Sentry.captureException('Debugg message: Failed to parse (6) following number:' + tooltipItem.raw);
              return tooltipItem.raw as string;
            }
          }
        }
      },
    },
    scales: {
      xAxis: {
        type: 'time',
        display: false,
      },
      yAxis: {
        beginAtZero: true,
        ticks: {
          display: true,
          autoSkip: true,
          maxTicksLimit: 3,
          callback: function(value) {
            return value + ' %';
          }
        }
      }
    },
    elements: {
      line: {
        borderWidth: 3,
      }
    }
  };

  public constructor(
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private restApiService: RestApiService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  public ngOnInit(): void {
    this.route.paramMap.pipe(take(1)).subscribe((params: ParamMap) => {
      // for portfolios/detail where investments are static so onChange is useless
      if(this.forceInitDataLoad(params)) {
        this.loadData();
      } else {
        this.changesSub = this.changesSubject.pipe(
          distinctUntilChanged((prev, curr) => !this.hasWeightChanged(prev['cardData'].currentValue['chartData'], curr['cardData'].currentValue['chartData'])),
          debounceTime(300)
        ).subscribe(() => {
          this.loadData();
        });
      }
    })
  }

  private forceInitDataLoad(params: ParamMap): boolean {
    return (params.get('investmentId') && this.router.url.includes('investment-detail')) || this.router.url.includes('portfolio/new') || this.router.url.includes('suggest-portfolio');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.changesSubject.next(changes);
  }

  private bindTooltips(): void {
    if(this.disabledButton3YContent && this.tooltip3YContainer) {
      tippy(this.disabledButton3YContent.nativeElement, {
        content: this.tooltip3YContainer.nativeElement.innerHTML,
        allowHTML: true,
        placement: 'auto',
      });
    }
    if(this.disabledButton5YContent && this.tooltip5YContainerEl) {
      tippy(this.disabledButton5YContent.nativeElement, {
        content: this.tooltip5YContainerEl.nativeElement.innerHTML,
        allowHTML: true,
        placement: 'auto',
      });
    }
  }

  private hasWeightChanged(prevValue: ModelPortfolioInstruments[], nextValue: ModelPortfolioInstruments[]): boolean {
    const prevMap: Record<string, number> = {};

    for (const obj of prevValue) {
        prevMap[obj.instrument.isin] = obj.weight;
    }

    // Check if any weight has changed
    for (const obj of nextValue) {
      const prevWeight = prevMap[obj.instrument.isin];
        if (prevWeight !== obj.weight) {
          return true;
        }
      }

    return false;
  }

  private loadData(): void {
    if(!this.cardData) {
      console.error('Error, missing cardData:', this.cardData);
      this.preloader = false;
      this.loadError = true;
      return;
    }

    this.clearErrors();
    const isinsToWeights = mapIsinsToWeights(this.cardData);

    // todo exclude when sum is not 1.0 with error
    if(this.shouldCallApi(isinsToWeights)) {
      this.restApiService.getNewInvestmentHistoryChart(isinsToWeights).subscribe({
        error: this.handleChartError.bind(this),
        next: this.handleChartData.bind(this),
      });
    } else {
      // fill with selected instruments and "empty" api data to display selected instruments
      this.updateDropdowns();
      this.preloader = false;
      this.noDataAvailable = true;
    }
  }

  private clearErrors(): void {
    this.preloader = true;
    this.loadError = false;
    this.wrongWeights = false;
    this.weightNull = false;
  }

  private shouldCallApi(isinsToWeights: Record<string, number>): boolean {
    let weightsSum = 0;

    // check weigths
    for (const key in isinsToWeights) {
      if (key in isinsToWeights) {
        if(isinsToWeights[key] === 0) {
          this.weightNull = true;
          return false;
        }
        weightsSum += isinsToWeights[key];
      }
    }
    if (weightsSum.toFixed(2) !== '1.00') {
      this.wrongWeights = true;
      return false;
    }
    // only cash can be alone, dont call
    if (Object.keys(isinsToWeights).length <= 1) {
      return false;
    }

    return true;
  }

  private handleChartData(data: InstrumentPredictionOut): void {
    this.chartData = this.parseApiResponse(data);
    const preselectChartYear = this.preselectChart();
    if(preselectChartYear) {
      this.updateChart(preselectChartYear);
    } else {
      this.updateDropdowns();
    }
    this.preloader = false;
    // wait for preloader element to re-render to display bindable elements
    setTimeout(() => {
      this.bindTooltips();
    }, 10);
  }

  private preselectChart(): 'oneYear' | 'threeYears' | 'fiveYears' | null {
    if(!this.chartData) {
      this.availableYearsBack = 1;
      return 'oneYear';
    }
    this.noDataAvailable = false;
    if (this.chartData.fiveYears.graph.length > 1) {
      this.availableYearsBack = 5;
      return 'fiveYears';
    } else if (this.chartData.threeYears.graph.length > 1) {
      this.availableYearsBack = 3;
      return 'threeYears';
    } else if (this.chartData.oneYear.graph.length > 1) {
      this.availableYearsBack = 1;
      return 'oneYear';
    } else {
      this.noDataAvailable = true;
      return null;
    }
  }

  private parseApiResponse(data: InstrumentPredictionOut): InstrumentPredictionOut {
    for (const key in data) {
      for (const graphPoint of data[key].graph) {
        graphPoint.x = graphPoint.x * 1000; // unix time
        graphPoint.y = graphPoint.y * 100; // percent, 1.0 = 100%
      }
      for (const instrument of data[key].performances) {
        instrument.performance = instrument.performance * 100; // percent, 1.0 = 100%
      }
    }

    return data;
  }

  public updateChart(next: 'oneYear' | 'threeYears' | 'fiveYears'): void {
    if(this.chartData) {
      this.selectedYears = next;
      const nextChart = this.chartData[next];

      this.updateDropdowns(nextChart);

      const labels: number[] = nextChart.graph.map(chartPoint => chartPoint.x);

      this.selectedChartData = {
        datasets: [{
            data: nextChart.graph,
            showLine: true,
            pointHitRadius: 8,
            pointRadius: 0,
            pointHoverRadius: 0,
        }],
        labels,
      };
    }
  }

  private updateDropdowns(nextChart: InstrumentPredictionIntervalOut = {graph: [], performances: [], cumulativePriceAction: NaN}): void {
    const cashNameTranslated = (this.translate.instant('global.cash') + '').charAt(0).toUpperCase()
      + (this.translate.instant('global.cash') + '').slice(1);
    this.dropdownData = this.cardData?.chartData.map(selectedInstrument => {
      // fixme - sometimes is comming `instrumentId` and sometimes just `id`
      const fullLoopedInstrument = nextChart.performances.find(
        instrument => (selectedInstrument.instrument.instrumentId === instrument.instrumentId || selectedInstrument.instrument['id'] === instrument.instrumentId)
      );

      return {
        humanName: selectedInstrument.instrument.cash ? cashNameTranslated : selectedInstrument.instrument.humanName,
        performance: fullLoopedInstrument ? fullLoopedInstrument.performance : NaN
      }
    }) ?? [];
  }

  private handleChartError(error: unknown): void {
    this.preloader = false;
    this.loadError = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.historyChartLoadError',
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  public ngOnDestroy(): void {
    this.changesSub?.unsubscribe();
  }
}
